'use client';

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Typography } from '../../../components';
import { useTranslations } from '../../../contexts/translation.context';
import FiltersSection from './components/FiltersSection';
import SubscribersTable from './components/SubscribersTable';
import { SubscriberData, SubscribersFilters, SubscribersFiltersProps } from './types';
import { transformSubscriberData } from './utils';
import {
  DEFAULT_FILTERS,
  DEFAULT_PAGE_SIZE,
  SEARCH_DEBOUNCE_DELAY,
  MODELS_PAGE_SIZE,
  MODEL_SEARCH_DEBOUNCE_DELAY,
} from './constants';
import { clientStatisticService } from '../../../api';
import clientAccService from '../../../api/client/clientAccService';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';

// API call to fetch subscribers using service
const fetchSubscribers = async (params: any) => {
  try {
    const response = await clientStatisticService.getSubscribers(params);
    return response.data;
  } catch (error) {
    console.error('Error fetching subscribers:', error);
    throw error;
  }
};

const Subscribers: React.FC = () => {
  const { t } = useTranslations();
  const [subscribers, setSubscribers] = useState<SubscriberData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState<string>('created_at');
  const breadcrumbs = useMemo(
    () => [
      { label: t('sidebar.dashboard'), href: '/' },
      { label: t('sidebar.subscribers'), isActive: true },
    ],
    [t]
  );
  usePageBreadcrumbs(breadcrumbs);
  const [filters, setFilters] = useState<SubscribersFilters>(DEFAULT_FILTERS);

  // Models and accounts state
  const [availableModels, setAvailableModels] = useState<
    { id: string; name: string; username: string; avatar?: string }[]
  >([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelSearchTerm, setModelSearchTerm] = useState('');

  const [availableAccounts, setAvailableAccounts] = useState<
    { id: string; name: string; username: string; avatar?: string }[]
  >([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');

  // Local states for debounced inputs
  const [localSearch, setLocalSearch] = useState(filters.search);
  const [localCampaignId, setLocalCampaignId] = useState(filters.campaignId || '');

  const loadModels = useCallback(async (searchTerm: string = '') => {
    setModelsLoading(true);
    try {
      const response = await clientAccService.getAccounts({
        page: 1,
        page_size: MODELS_PAGE_SIZE,
        search: searchTerm.trim() || undefined,
      });

      if (
        response &&
        response.data &&
        response.data.objects &&
        Array.isArray(response.data.objects)
      ) {
        const models = response.data.objects.map((account: any) => ({
          id: account.id,
          name: account.name,
          username: account.of_user_name,
          avatar: account.avatar,
        }));
        setAvailableModels(models);
      } else {
        setAvailableModels([]);
      }
    } catch (error) {
      setAvailableModels([]);
    } finally {
      setModelsLoading(false);
    }
  }, []);

  const loadAccounts = useCallback(async (searchTerm: string = '') => {
    setAccountsLoading(true);
    try {
      const response = await clientAccService.getAccounts({
        page: 1,
        page_size: MODELS_PAGE_SIZE,
        search: searchTerm.trim() || undefined,
      });

      if (
        response &&
        response.data &&
        response.data.objects &&
        Array.isArray(response.data.objects)
      ) {
        const accounts = response.data.objects.map((account: any) => ({
          id: account.id,
          name: account.name,
          username: account.of_user_name,
          avatar: account.avatar,
        }));
        setAvailableAccounts(accounts);
      } else {
        setAvailableAccounts([]);
      }
    } catch (error) {
      setAvailableAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!modelSearchTerm.trim()) {
      loadModels();
      return;
    }

    const timer = setTimeout(() => {
      loadModels(modelSearchTerm);
    }, MODEL_SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [modelSearchTerm, loadModels]);

  useEffect(() => {
    if (!accountSearchTerm.trim()) {
      loadAccounts();
      return;
    }

    const timer = setTimeout(() => {
      loadAccounts(accountSearchTerm);
    }, MODEL_SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [accountSearchTerm, loadAccounts]);

  // Debounce for search input
  useEffect(() => {
    if (localSearch === filters.search) return;

    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: localSearch }));
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localSearch, filters.search]);

  // Debounce for campaign ID input
  useEffect(() => {
    if ((localCampaignId || null) === (filters.campaignId ?? null)) return;

    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, campaignId: localCampaignId || null }));
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localCampaignId, filters.campaignId]);

  // Load subscribers when dependencies change
  useEffect(() => {
    const loadSubscribers = async () => {
      setIsLoading(true);
      try {
        // Маппим paymentTypes чекбоксы на API параметры
        const apiParams: any = {
          page: currentPage,
          page_size: pageSize,
          search: filters.search || undefined,
          campaign_id: filters.campaignId || undefined,
          account: filters.account !== 'all' ? filters.account : undefined,
          models: filters.models.length > 0 ? filters.models : undefined,
          accounts: filters.accounts.length > 0 ? filters.accounts : undefined,
          model_id: filters.models.length > 0 ? filters.models[0] : filters.model_id || undefined,
          sort_by: sortBy,
        };

        // Обрабатываем выбранные чекбоксы как API параметры
        filters.paymentTypes.forEach((paymentType) => {
          if (paymentType === 'has_gte_three_messages') {
            apiParams.has_gte_three_messages = true;
          } else if (paymentType === 'one_transaction') {
            apiParams.one_transaction = true;
          } else if (paymentType === 'two_transaction') {
            apiParams.two_transaction = true;
          } else if (paymentType === 'five_transaction') {
            apiParams.five_transaction = true;
          } else if (paymentType === 'amount_more_500') {
            apiParams.amount_more_500 = true;
          }
        });

        const hasPaidSubscription = filters.paymentTypes.includes('paid_subscription');
        const hasFreeSubscription = filters.paymentTypes.includes('free_subscription');
        if (hasPaidSubscription && !hasFreeSubscription) {
          apiParams.paid = true;
        } else if (hasFreeSubscription && !hasPaidSubscription) {
          apiParams.paid = false;
        }

        const response = await fetchSubscribers(apiParams);

        if (response && response.objects && Array.isArray(response.objects)) {
          const transformedSubscribers = response.objects.map(transformSubscriberData);
          setSubscribers(transformedSubscribers);
          setTotalCount(response.count || 0);
        } else {
          setSubscribers([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.error('Failed to load subscribers:', error);
        setSubscribers([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    loadSubscribers();
  }, [currentPage, pageSize, filters, sortBy]);

  const filteredSubscribers = useMemo(() => {
    let result = subscribers;

    if (filters.search) {
      result = result.filter((subscriber) => {
        const searchLower = filters.search.toLowerCase();
        return (
          subscriber.id.toLowerCase().includes(searchLower) ||
          subscriber.name.toLowerCase().includes(searchLower) ||
          subscriber.username.toLowerCase().includes(searchLower) ||
          subscriber.model.name.toLowerCase().includes(searchLower) ||
          subscriber.model.ofUserName.toLowerCase().includes(searchLower)
        );
      });
    }

    return result;
  }, [subscribers, filters.search]);

  const handleFilterChange = useCallback(
    (key: keyof SubscribersFilters, value: string | string[] | number | null) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      setCurrentPage(1);
    },
    []
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  }, []);

  const handleRefresh = useCallback(() => {
    // Trigger a reload by updating a dependency
    setCurrentPage((prev) => prev);
  }, []);

  const handleSort = useCallback((newSort: string) => {
    setSortBy(newSort);
    setCurrentPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setLocalSearch('');
    setLocalCampaignId('');
    setSortBy('created_at');
    setCurrentPage(1);
  }, []);

  // Проверяем, есть ли активные фильтры
  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== '' ||
      filters.campaignId !== null ||
      filters.account !== 'all' ||
      filters.paymentTypes.length > 0 ||
      filters.models.length > 0 ||
      filters.accounts.length > 0 ||
      filters.model_id !== null
    );
  }, [filters]);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-4 py-6 md:py-4 md:px-6">
        <Typography variant="h5" className="md:font-semibold text-gray-900">
          {t('subscribers.title')}
        </Typography>
      </div>

      <div className="px-4 pb-6 md:px-6 md:pb-6">
        <FiltersSection
          filters={filters}
          localSearch={localSearch}
          localCampaignId={localCampaignId}
          availableModels={availableModels}
          modelsLoading={modelsLoading}
          availableAccounts={availableAccounts}
          accountsLoading={accountsLoading}
          onSearchChange={setLocalSearch}
          onCampaignIdChange={setLocalCampaignId}
          onFilterChange={handleFilterChange}
          onModelSearchChange={setModelSearchTerm}
          onAccountSearchChange={setAccountSearchTerm}
          onRefresh={handleRefresh}
          isRefreshing={isLoading}
          hasActiveFilters={hasActiveFilters}
          onResetFilters={handleResetFilters}
        />
      </div>

      {/* Table */}
      <div className="px-4 md:px-6 md:h-full">
        <SubscribersTable
          subscribers={filteredSubscribers}
          isLoading={isLoading}
          totalCount={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          sortBy={sortBy}
          onSort={handleSort}
        />
      </div>
    </div>
  );
};

export default Subscribers;
